import { MenuButton, StatusChip } from "@msys/ui";
import { KeyboardArrowDown as KeyboardArrowDown } from "@mui/icons-material";
import { MenuItem } from "@mui/material";
import React from "react";
import { useUserData } from "../../../auth/useUserData.js";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../../commons/modals/ConfirmProcess.js";
import { ProjectStateMachineStatus } from "../../../../clients/graphqlTypes.js";
import { useProjectChangeState } from "../useProjectChangeState.js";
import {
  ALL_PROJECT_EVENTS,
  getPossibleProjectStateEvents,
  projectStateChipColors,
  useProjectStates,
} from "../useProjectStates.js";
import { ProjectStatusChip } from "./ProjectStatusBadge.js";

interface Props {
  canEdit: boolean;
  projectId: string;
  projectState: ProjectStateMachineStatus;
  size?: React.ComponentProps<typeof StatusChip>["size"];
}

export const ProjectStatusChipWithEditing = ({
  canEdit,
  projectId,
  projectState,
  size = "small",
}: Props) => {
  const viewer = useUserData().currentUser!;
  const events = getPossibleProjectStateEvents(projectState, {
    guards: { isContractor: viewer.organisation.isCraftsmanOrganisation },
  });
  const { projectStateLabels, projectStateEventLabels } = useProjectStates();
  const [open, setOpen] = React.useState<boolean>(false);

  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);
  const { handleStateChange } = useProjectChangeState({
    projectId,
    projectState,
    confirmProcessRef,
  });

  return canEdit && events.length > 0 ? (
    <>
      <MenuButton
        dataTestId="project-status-chip"
        Button={
          <StatusChip
            label={projectStateLabels[projectState]}
            color={projectStateChipColors[projectState]}
            size={size}
            endIcon={
              <KeyboardArrowDown
                fontSize="small"
                sx={{
                  transition: "transform 0.2s ease-out",
                  ...(open ? { transform: `rotate(-180deg)` } : undefined),
                }}
              />
            }
          />
        }
        onToggle={setOpen}
      >
        {ALL_PROJECT_EVENTS.filter(e => events.includes(e)).map(event => (
          <MenuItem
            key={event}
            onClick={async () => {
              await handleStateChange(event);
            }}
          >
            {projectStateEventLabels[event]}
          </MenuItem>
        ))}
      </MenuButton>
      <ConfirmProcess ref={confirmProcessRef} />
    </>
  ) : (
    <ProjectStatusChip projectState={projectState} size={size} />
  );
};
